<template>
  <v-dialog
    :value="dialog"
    :max-width="400"
    persistent
    :retain-focus="false"
    @keydown.esc="escape(closeDialog)"
    @click:outside="escape(closeDialog)"
    @keydown.enter="submit"
  >
    <v-card outlined>
      <v-card-title>
        {{ budgetCategory ? $t('budgetCategory.editCategory') : $t('budgetCategory.createCategory') }}
        <v-spacer></v-spacer>
        <p-btn-icon
          type="close"
          btn-class="mr-n3 ml-auto"
          tabindex="4"
          @click="escape(closeDialog)"
        ></p-btn-icon>
      </v-card-title>

      <v-divider></v-divider>

      <v-card-text>
        <v-form ref="form" @submit.stop.prevent>
          <v-container>
            <v-row dense align="center" justify="end">
              <v-col>
                <v-text-field
                  type="text"
                  v-model.trim="editedBudgetCategory.name"
                  :label="$t('budgetCategory.edit.name')"
                  :rules="rules.name"
                  tabindex="1"
                  validate-on-blur
                  clearable
                  autofocus
                ></v-text-field>
                <v-text-field
                  type="text"
                  v-model.trim="editedBudgetCategory.account"
                  :label="$t('budgetCategory.edit.account')"
                  :rules="rules.account"
                  tabindex="1"
                  clearable
                  validate-on-blur
                ></v-text-field>
                <p-number-input
                  v-model="editedBudgetCategory.budget"
                  :label="$t('budgetCategory.edit.budget')"
                  tabindex="1"
                  clearable
                ></p-number-input>
                <v-radio-group
                  v-model="editedBudgetCategory.income"
                  row
                >
                  <v-radio
                    :label="$t('budgetCategory.edit.income')"
                    :value="true"
                    :disabled="incomeDisabled"
                    tabindex="1"
                  ></v-radio>
                  <v-radio
                    :label="$t('budgetCategory.edit.expense')"
                    :value="false"
                    :disabled="incomeDisabled"
                    tabindex="1"
                  ></v-radio>
                </v-radio-group>
              </v-col>
            </v-row>
          </v-container>
        </v-form>
      </v-card-text>

      <v-divider></v-divider>

      <v-card-actions class="justify-center">
        <v-btn text tabindex="3" @click="closeDialog">{{ $t('cancel') }}</v-btn>
        <v-btn text :disabled="!isModelChanged()" tabindex="2" color="primary" @click="submit">{{ $t('save') }}</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>


<script>
import { mapActions, mapGetters } from 'vuex'

import PNumberInput from '@/components/PNumberInput'
import PBtnIcon from '@/components/PBtnIcon'

import snackbarMixin from '@/mixins/snackbar.mixin'
import objectMixin from '@/mixins/object.mixin'
import dialogMixin from '@/mixins/dialog.mixin'
import validationMixin from '@/mixins/validation.mixin'
import unsavedChangesMixin from '@/mixins/unsavedChanges.mixin'
import errorMixin from '@/mixins/error.mixin'


export default {
  components: {
    PNumberInput,
    PBtnIcon
  },


  mixins: [
    snackbarMixin,
    objectMixin,
    dialogMixin,
    validationMixin,
    unsavedChangesMixin,
    errorMixin
  ],


  props: {
    budgetCategory: Object,
    income: {
      type: Boolean,
      default: true
    },
    incomeDisabled: {
      type: Boolean,
      default: false
    }
  },


  data () {
    return {
      editedBudgetCategory: {},
      defaultBudgetCategory: {
        name: '',
        account: '',
        income: this.income,
        budget: 0
      },
      existingBudgetCategoryNames: []
    }
  },


  computed: {
    ...mapGetters('season', ['currentSeasonId']),

    rulesToApply () {
      const rule = this.rule

      return {
        name: [
          rule.required(),
          rule.unique(this.existingBudgetCategoryNames, this.$t('budgetCategory.edit.nameExists'))
        ],
        account: [rule.required()]
      }
    }
  },


  methods: {
    ...mapActions('budgetCategory', ['createBudgetCategory', 'updateBudgetCategory']),
    ...mapActions('changes', ['removeFromChanges']),

    onDialogOpen () {
      if (this.budgetCategory) {
        this.removeFromChanges(this.budgetCategory)
      }
      this.existingBudgetCategoryNames = []
      this.editedBudgetCategory = this.getObjectCopy(this.budgetCategory || this.defaultBudgetCategory)
      this.initWatchedModel(this.editedBudgetCategory)
      this.resetValidation()
    },

    async submit () {
      const valid = await this.validate(this.rulesToApply)
      if (!valid || !this.isModelChanged()) return

      try {
        this.showSnackbarProcessing()
        const budgetCategory = this.getObjectCopy(this.editedBudgetCategory)
        budgetCategory.budget = parseInt(budgetCategory.budget)

        if (budgetCategory.id) {
          await this.updateBudgetCategory(budgetCategory)
          this.showSnackbarSuccess(this.$t('budgetCategory.categoryUpdated'))
        } else {
          budgetCategory.seasonId = this.currentSeasonId
          const addedBudgetCategory = await this.createBudgetCategory(budgetCategory)
          this.showSnackbarSuccess(this.$t('budgetCategory.categoryCreated'))
          this.$emit('add', addedBudgetCategory)
        }

        this.closeDialog()
      } catch (error) {
        if (this.is409ConflictError(error)) {
          this.hideSnackbar()
          this.existingBudgetCategoryNames.push(this.editedBudgetCategory.name)
          this.validate(this.rulesToApply)
        } else {
          this.showSnackbarError()
        }
        this.$log.error(error)
      }
    }
  }
}
</script>
